import React from 'react';
import { Text, Heading } from '@wix/design-system';
import { Clock, Copy, Check } from 'lucide-react';
import { useTranslation } from '@wix/wix-i18n-config';
import { COUPON_CODE_50_YEARLY } from '../../../../consts';
import { useCopyCode } from '../../hooks/useCopyCode';
import { PromotionalOfferProps } from './types';
import s from './PromotionalOffer.scss';
import { useViewport } from '../../../../context';

export const PromotionalOffer = ({
  variant = 'desktop',
  onGetOffer,
  onCopyCode,
  disabled = false,
}: PromotionalOfferProps) => {
  const { t } = useTranslation();
  const { isMobile } = useViewport();
  const { copied, handleCopyCode } = useCopyCode(onCopyCode);

  const handleCopyCodeClick = () => {
    handleCopyCode(COUPON_CODE_50_YEARLY);
  };

  const handleGetOffer = () => {
    if (!disabled && onGetOffer) {
      handleCopyCode(COUPON_CODE_50_YEARLY);
      onGetOffer();
    }
  };

  if (variant === 'button') {
    return (
      <button
        type="button"
        className={`${s.promotionalOffer} ${s['promotionalOffer--button']} ${disabled ? s.disabled : ''}`}
        onClick={handleGetOffer}
        disabled={disabled}
      >
        <Clock size={16} className={s.clockIcon} />
        <h1 className={s.buttonText}>{t('promotion_code.cta')}</h1>
      </button>
    );
  }

  return (
    <div className={`${s.promotionalOffer} ${s[`promotionalOffer--${variant}`]}`}>
      <div className={s.offerHeader}>
        {isMobile ? (
          <div className={s.limitedOffer}>
            <Heading className={s.limitedOfferText} size="extraLarge">
              {t('promotion_code.mobile_header').toUpperCase()}
            </Heading>
          </div>
        ) : (
          <div className={s.limitedOffer}>
            <Clock size={16} className={s.clockIcon} />
            <Heading className={s.limitedOfferText} weight="bold" size="extraLarge">
              {t('promotion_code.desktop_header').toUpperCase()}
            </Heading>
          </div>
        )}

        <Heading className={s.mainHeadline} weight="bold" size="extraLarge">
          {t('promotion_code.title').toUpperCase()}
        </Heading>

        <Text className={s.description} weight={isMobile ? 'normal' : 'bold'}>
          {isMobile ? t('promotion_code.mobile_description') : t('promotion_code.description')}
        </Text>
      </div>

      <div className={s.couponSection}>
        <Text className={s.couponInstruction}>
          {isMobile ? t('promotion_code.mobile_instructions') : t('promotion_code.instructions')}
        </Text>

        <div className={s.couponCodeContainer}>
          <Heading
            className={s.couponCode}
            size={isMobile ? 'large' : 'medium'}
            onClick={handleCopyCodeClick}
            style={{ cursor: 'pointer' }}
          >
            {COUPON_CODE_50_YEARLY}
          </Heading>
          <button
            type="button"
            className={s.copyButton}
            onClick={handleCopyCodeClick}
            aria-label="Copy coupon code"
          >
            {copied ? (
              <Check size={isMobile ? 14 : 16} className={s.checkIcon} />
            ) : (
              <Copy size={isMobile ? 14 : 16} className={s.copyIcon} />
            )}
          </button>
        </div>
      </div>

      {!isMobile && (
        <button className={s.ctaButton} onClick={handleGetOffer}>
          <Text className={s.ctaButtonText} weight="bold">
            {t('promotion_code.cta')}
          </Text>
        </button>
      )}
    </div>
  );
};
